import { ScenarioModel } from "../scenario/scenario.model";
import { observable, computed, action, runInAction } from "mobx";
import {
  SessionData,
  GameSessionModelData,
  GameReleaseData,
  FileCollector,
  GameModelDataWithoutSession,
} from "@citychallenge/common";
import { FileService } from "../../services/file";
import { DownloadTask } from "../../download";

export class GameBaseModel
  implements GameModelDataWithoutSession<ScenarioModel>
{
  /**
   * Constructor\
   * @param data Game model data
   * @param fileService File service so that the model can create download tasks
   * for any files that need to be downloaded
   */
  constructor(
    data: GameModelDataWithoutSession,
    public fileService: FileService
  ) {
    this.id = data.id;
    this.name = data.name;
    this.image = data.image;
    this.defaultColorTheme = data.defaultColorTheme;
    this.disableOutboundUtm = data.disableOutboundUtm;
    this.enableContentAnimations = data.enableContentAnimations;
    this.enableIconOverlay = data.enableIconOverlay;
    this.enableTapToSkip = data.enableTapToSkip;
    this.maximizeAccessibility = data.maximizeAccessibility;
    this.storyIcon = data.storyIcon;

    this.divisionVariables = data.divisionVariables || {};
    this.variables = data.variables || {};

    this.reviewDone = data.reviewDone || false;

    this.branches = data.branches?.map(
      (branch) => new ScenarioModel(branch, this)
    );

    this.release = {
      id: data.release.id,
      releaseVersion: data.release.releaseVersion,
      languages: data.release.languages,
      game: { id: data.id },
    };

    this.baseScenario = new ScenarioModel(data.baseScenario, this);
  }

  public readonly session?: SessionData;

  public readonly id: string;

  @observable
  public name: string;

  @observable
  public readonly image?: string;

  @observable
  public readonly baseScenario: ScenarioModel;

  @observable
  public branches?: ScenarioModel[];

  @observable
  public readonly release: GameReleaseData;

  @observable
  public reviewDone: boolean;

  @observable
  public defaultColorTheme?: string;

  @observable
  public disableOutboundUtm?: boolean;

  @observable
  public enableContentAnimations?: boolean;

  @observable
  public enableIconOverlay?: boolean | undefined;

  @observable
  public enableTapToSkip?: boolean | undefined;

  @observable
  public maximizeAccessibility?: boolean | undefined;

  @observable
  public storyIcon?: string;

  @action
  public setReviewDone = (done: boolean) => {
    this.reviewDone = done;
  };

  @computed
  public get totalPoints(): number {
    return this.baseScenario.scenes.reduce((prev, scene) => {
      return prev + scene.totalPoints;
    }, 0);
  }

  @computed
  public get maxPoints(): number {
    const scenarios = [this.baseScenario, ...(this.branches || [])];

    return scenarios.reduce(
      (totalPoints, scenario) =>
        totalPoints +
        scenario.scenes.reduce(
          (scenarioPoints, scene) => scenarioPoints + scene.maxPoints,
          0
        ),
      0
    );
  }

  public get allSceneStates() {
    return this.baseScenario.scenes.map((scene) => {
      return [
        scene.state,
        scene.collectedData,
        scene.collectedEvents,
        scene.collectedActions,
        scene.showingPoints,
        scene.showingExplanation,
        scene.hintsUsed,
      ];
    });
  }

  public get allCollectedEvents() {
    return this.baseScenario.scenes.map((scene) => {
      return scene.collectedEvents;
    });
  }

  @computed
  public get activeSceneIndex() {
    return this.baseScenario.activeSceneIndex;
  }

  @computed
  public get numScenes() {
    return this.baseScenario.numScenes;
  }

  @computed
  public get completionPercentage() {
    return this.baseScenario.progress;
  }

  @computed
  public get activeSceneSkippable() {
    if (this.finished) return false;
    return (
      this.baseScenario.activeSceneIsVisible &&
      this.baseScenario.activeSceneSkippable
    );
  }

  public skipActiveScene() {
    this.baseScenario.skipActiveScene();
  }

  @computed
  public get activeScene() {
    return this.baseScenario.activeScene;
  }

  @computed
  public get finished() {
    // Later on this can be a reducer which checks if all scenario's are finished
    return !!this.baseScenario.finished;
  }

  @computed
  public get progress() {
    return this.baseScenario.progress;
  }

  /**
   * Game progress rounded to whole numbers.
   */
  @computed
  public get progressRounded() {
    return Math.round(this.progress);
  }

  /**
   * This shared  file collector makes it possible for scenarios and scenes to
   * collect their files, and avoid duplicate files across a game
   */
  public readonly fileCollector = new FileCollector({
    skipAssignment: true,
    alwaysReturn: true,
  });

  @computed
  public get allDownloadTasks() {
    const tasks: DownloadTask[] = [];
    for (const { downloadStore } of this.baseScenario.scenes) {
      tasks.push(...downloadStore.all);
    }

    return tasks;
  }

  public findSceneById(id: string) {
    const scenarios = [this.baseScenario, ...(this.branches || [])];

    for (const scenario of scenarios) {
      for (const scene of scenario.scenes) {
        if (scene.id === id) return scene;
      }
    }

    return undefined;
  }

  @action
  public resetDownloadStates() {
    for (const { downloadStore } of this.baseScenario.scenes) {
      downloadStore.reset();
    }
  }

  @computed
  public get allMapInfo() {
    return this.baseScenario.allMapInfo;
  }

  @action
  public mergeWithData(data: GameModelDataWithoutSession) {
    this.name = data.name;

    const { branches = [] } = data;
    // Map updated branches
    this.branches = branches.map((branch) => new ScenarioModel(branch, this));

    const allScenes = data.baseScenario.scenes;

    for (const scenario of branches) {
      if (scenario?.scenes) allScenes.push(...scenario.scenes);
    }

    for (const scene of this.baseScenario.scenes) {
      const newScene = allScenes.find((s) => s.scene.id === scene.id);

      if (newScene) {
        scene.layout = newScene.scene.layout;
        scene.explanationText = newScene.scene.explanationText;
        scene.buttonText = newScene.scene.buttonText;
        scene.resetDownloadStore();
      }
    }
  }

  /**
   * This object contains variables that are set in the division. They are
   * kept in sync with the division variables in the division store by a
   * effect hook in the session controller.
   * However, they are not synced to the server nor are they stored in the
   * session data in localstorage.
   */
  @observable
  public divisionVariables: { [key: string]: string } = {};

  /**
   * Variables are set trough actions or start-of-game variables.
   * Opposed to collected data that is set in the scene, variables are always
   * to be prefixed by an ampersand (@) when stored in this object.
   */
  @observable
  public variables: { [key: string]: string } = {};

  @action
  public setVariables(params: { [key: string]: string }) {
    this.variables = { ...this.variables, ...params };
  }

  /* Deserialization */
  public toJSON(): GameModelDataWithoutSession {
    return {
      id: this.id,
      name: this.name,
      image: this.image,
      defaultColorTheme: this.defaultColorTheme,
      disableOutboundUtm: this.disableOutboundUtm,
      enableContentAnimations: this.enableContentAnimations,
      enableIconOverlay: this.enableIconOverlay,
      enableTapToSkip: this.enableTapToSkip,
      maximizeAccessibility: this.maximizeAccessibility,
      storyIcon: this.storyIcon,
      baseScenario: this.baseScenario.toJSON(),
      branches: this.branches?.map((branch) => branch.toJSON()),
      release: this.release,
      variables: this.variables,
      divisionVariables: this.divisionVariables,
      reviewDone: this.reviewDone,
    };
  }
}

class GameStatisticsModel {
  constructor(private game: GameSessionModel) {}

  private get session() {
    return this.game.session;
  }

  @computed
  public get battery() {
    return this.session.batteryFinished
      ? Math.round(this.session.batteryFinished - this.session.batteryStarted)
      : 0;
  }

  @computed
  public get batteryStats() {
    if (!this.session) return "n/a";

    let add = "";
    if (this.battery > 0) add = "+";
    return `${add}${this.battery}%`;
  }

  @computed
  public get minutes() {
    if (!this.session) return 0;

    const finished = this.session.finished
      ? +new Date(this.session.finished)
      : +new Date();
    const started = +new Date(this.session.started);
    const ms = finished - started;

    return Math.round(ms / 1000 / 60);
  }

  @computed
  public get meters() {
    if (!this.session) return 0;

    return this.session.gameInfo.meters;
  }

  @computed
  public get duration() {
    return `${this.minutes}`;
  }

  @computed
  public get distance() {
    if (this.meters === 0) return undefined;
    return `${this.meters}`;
  }

  @computed
  public get calories() {
    if (this.minutes === 0 || this.meters === 0) return undefined;
    const meters = this.meters;
    const weight = 65;

    const minutes = this.minutes;

    const kph = meters / (minutes / 60) / 1000;
    const kph2 = Math.pow(kph, 2);
    const kph3 = Math.pow(kph, 3);
    const calories =
      (0.0215 * kph3 - 0.1765 * kph2 + 0.871 * kph + 1.4577) *
      weight *
      (minutes / 60);
    return `${Math.round(calories)}`;
  }
}

export class GameSessionModel extends GameBaseModel {
  // Session Info
  @observable
  public readonly session: SessionData;

  constructor(data: GameSessionModelData, public fileService: FileService) {
    super(data, fileService);
    this.session = data.session;
  }

  @action
  public async checkGameFinished(batteryLevel: () => Promise<number>) {
    const currentBatteryLevel = await batteryLevel();
    runInAction(() => {
      this.session.batteryFinished = currentBatteryLevel;
      if (
        this.completionPercentage === 100 &&
        this.session &&
        !this.session.finished
      ) {
        this.session.finished = new Date();
      }
    });
  }

  public readonly statistics = new GameStatisticsModel(this);

  @computed
  public get hideLeaderboard() {
    return this.session.hideLeaderboard;
  }

  public setSessionFinished() {
    if (this.session) this.session.finished = new Date();
  }

  @computed
  public get hasMetricsToShow(): boolean {
    const hasStatistics =
      !!this.statistics.minutes ||
      !!this.statistics.battery ||
      !!this.statistics.meters;

    return !!this.maxPoints && hasStatistics;
  }

  @observable
  public didShowConfetti = false;

  @action
  public setDidShowConfetti(bool: boolean) {
    this.didShowConfetti = bool;
  }

  public toJSON(): GameSessionModelData {
    return {
      ...super.toJSON(),
      session: this.session,
      didShowConfetti: this.didShowConfetti,
    };
  }
}
